import { useState } from "react";

interface Device {
  id: string;
  type: string;
  model: string;
  spec: string;
  manufacturer: string;
  productionDate: string;
  installDate: string;
  calibrationDate: string;
  nextCalibration: string;
  status: string;
  iotId: string;
  currentReading: number | null;
  pressure?: string;
  temperature?: string;
}

interface DevicePanelProps {
  devices: Device[];
}

const deviceIcons: Record<string, string> = {
  "燃气表": "ri-dashboard-line",
  "流量计": "ri-speed-line",
  "报警器": "ri-alarm-warning-line",
  "压力变送器": "ri-sensor-line",
};

const deviceColors: Record<string, string> = {
  "燃气表": "bg-orange-50 text-[#E8531A]",
  "流量计": "bg-teal-50 text-teal-600",
  "报警器": "bg-red-50 text-red-500",
  "压力变送器": "bg-green-50 text-green-600",
};

export default function DevicePanel({ devices }: DevicePanelProps) {
  const [selectedDevice, setSelectedDevice] = useState<Device>(devices[0]);

  const statusColor = (s: string) => {
    if (s === "正常") return "bg-green-50 text-green-600";
    if (s === "待校验") return "bg-amber-50 text-amber-600";
    return "bg-red-50 text-red-600";
  };

  const isCalibrationSoon = (date: string) => {
    const next = new Date(date);
    const now = new Date("2026-04-10");
    const diffDays = (next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays < 90;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800">计量设备信息</h3>
        <button className="flex items-center gap-1.5 text-xs text-[#E8531A] border border-[#E8531A]/30 px-2.5 py-1 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>新增设备
        </button>
      </div>

      {/* Device tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {devices.map(d => (
          <button
            key={d.id}
            onClick={() => setSelectedDevice(d)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              selectedDevice.id === d.id
                ? "bg-[#E8531A] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${deviceIcons[d.type] || "ri-cpu-line"} text-sm`}></i>
            </div>
            {d.type}
          </button>
        ))}
      </div>

      {/* Device detail */}
      <div className="border border-gray-100 rounded-xl p-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${deviceColors[selectedDevice.type] || "bg-gray-50 text-gray-500"}`}>
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${deviceIcons[selectedDevice.type] || "ri-cpu-line"} text-xl`}></i>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">{selectedDevice.model}</h4>
              <p className="text-xs text-gray-400 mt-0.5">IoT编号：{selectedDevice.iotId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(selectedDevice.status)}`}>
              {selectedDevice.status}
            </span>
            {isCalibrationSoon(selectedDevice.nextCalibration) && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-600 font-medium">
                校验即将到期
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
          {[
            { label: "设备规格", value: selectedDevice.spec },
            { label: "生产厂家", value: selectedDevice.manufacturer },
            { label: "出厂日期", value: selectedDevice.productionDate },
            { label: "安装日期", value: selectedDevice.installDate },
            { label: "上次校验", value: selectedDevice.calibrationDate },
            { label: "下次校验", value: selectedDevice.nextCalibration },
          ].map((item, i) => (
            <div key={i}>
              <p className="text-xs text-gray-400">{item.label}</p>
              <p className="text-sm font-medium text-gray-800 mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Real-time data */}
        {(selectedDevice.currentReading !== null || selectedDevice.pressure || selectedDevice.temperature) && (
          <div className="mt-4 pt-4 border-t border-gray-50">
            <p className="text-xs text-gray-400 mb-3">实时采集数据</p>
            <div className="flex gap-4 flex-wrap">
              {selectedDevice.currentReading !== null && (
                <div className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-dashboard-line text-[#E8531A] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">当前读数</p>
                    <p className="text-sm font-bold text-[#E8531A]">{selectedDevice.currentReading.toLocaleString()} m³</p>
                  </div>
                </div>
              )}
              {selectedDevice.pressure && (
                <div className="flex items-center gap-2 bg-teal-50 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-sensor-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">管道压力</p>
                    <p className="text-sm font-bold text-teal-600">{selectedDevice.pressure}</p>
                  </div>
                </div>
              )}
              {selectedDevice.temperature && (
                <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-temp-hot-line text-green-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">气体温度</p>
                    <p className="text-sm font-bold text-green-600">{selectedDevice.temperature}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-wifi-line text-gray-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">在线状态</p>
                  <p className="text-sm font-bold text-green-600">在线</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-edit-line mr-1"></i>编辑设备
          </button>
          <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-history-line mr-1"></i>维护记录
          </button>
          <button className="px-3 py-1.5 text-xs border border-amber-200 text-amber-600 rounded-lg hover:bg-amber-50 cursor-pointer whitespace-nowrap">
            <i className="ri-tools-line mr-1"></i>申请校验
          </button>
        </div>
      </div>
    </div>
  );
}
